<template>
  <div>
    <v-dialog
      v-model="internalValue"
      max-width="900px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header" data-tour="cpa-header">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-account-group</v-icon>
          <span class="ds-dialog-title">Pacientes desta Clínica</span>
          <v-spacer />
          <v-btn icon color="white" aria-label="Ver tour" class="close-btn" @click="startTour">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon color="white" class="close-btn" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- LISTA DE PACIENTES -->
          <!-- Informações da clínica -->
          <section v-if="selectedClinicInfo" class="ds-form-section" data-tour="cpa-clinic-info">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-hospital-building</v-icon>
                <span>Clínica</span>
              </div>
            </div>
            <div class="clinic-info-name">
              {{ selectedClinicInfo.name }}
            </div>
            <div class="clinic-info-meta">
              CNPJ: {{ selectedClinicInfo.cnpj }} -
              {{ selectedClinicInfo.city }}/{{
                selectedClinicInfo.state
              }}
            </div>
          </section>

          <!-- Botão de adicionar removido -->

          <!-- Lista de pacientes relacionados -->
          <section class="ds-form-section">
            <div class="ds-form-section__head" data-tour="cpa-list">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-account-group</v-icon>
                <span>Pacientes Vinculados</span>
              </div>
              <span v-if="!loadingRelatedPatients" class="ds-form-section__aside">{{ patientsRelated.length }}</span>
            </div>

            <v-container fluid class="pa-0">
              <v-row
                v-if="loadingRelatedPatients"
                justify="center"
                class="my-10"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="50"
                ></v-progress-circular>
              </v-row>

              <v-row
                v-if="!loadingRelatedPatients && patientsRelated.length"
                dense
              >
                <v-col
                  v-for="(patient, i) in patientsRelated"
                  :key="patient.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card
                    outlined
                    class="patient-card h-100"
                    hover
                    :data-tour="i === 0 ? 'cpa-patient-card' : null"
                  >
                    <v-card-title class="pa-3 flex-nowrap align-start">
                      <v-avatar
                        size="50"
                        class="mr-3 flex-shrink-0"
                        :data-tour="i === 0 ? 'cpa-avatar' : null"
                      >
                        <v-img :src="patient.imageUrl" />
                      </v-avatar>
                      <div
                        class="flex-grow-1"
                        style="min-width: 0"
                        :data-tour="i === 0 ? 'cpa-patient-info' : null"
                      >
                        <div
                          class="font-weight-medium text-caption text-truncate"
                          :title="patient.name"
                        >
                          {{ patient.name }}
                        </div>
                        <div
                          class="text-caption grey--text text-truncate"
                          :title="patient.email"
                        >
                          {{ patient.email }}
                        </div>
                        <div
                          class="text-caption grey--text text-truncate"
                          v-if="patient.cpf"
                        >
                          CPF: {{ patient.cpf }}
                        </div>
                      </div>
                    </v-card-title>

                    <v-card-actions class="pa-3 pt-0" v-if="false">
                      <!-- Ações removidas - apenas visualização -->
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <div
                v-else-if="!loadingRelatedPatients"
                class="text-center py-6"
              >
                <v-icon size="64" color="#C2DDD3" class="mb-3">
                  mdi-account-heart
                </v-icon>
                <div class="empty-state-title mb-2">
                  Nenhum paciente vinculado
                </div>
                <div class="empty-state-subtitle">
                  Esta clínica ainda não possui pacientes vinculados
                </div>
              </div>
            </v-container>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { getMyClinics, getPatientsByClinics } from "@/services/clinicService";
import { getProfileImage } from "@/utils/getProfileImage";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "CheckMyClinicPatientsDialog",

  mixins: [tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: Number, default: null },
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      dialogVisible: false,

      // clinic
      clinics: [],
      selectedClinicId: null,
      selectedClinicInfo: null,

      // patients list
      patientsRelated: [],
      loadingRelatedPatients: false,

      // ✅ guarda blob urls (para revogar)
      _patientBlobUrls: new Set(),
    };
  },

  computed: {
    userType() {
      return this.$store.getters["auth/userType"];
    },

    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    tourId() {
      return "dialog-pacientes-clinica";
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.initDialog();
        this.tourOnOpen();
      }
    },
  },

  async mounted() {
    await this.$store.dispatch("auth/ensureAuth");
    await this.fetchClinics();
  },

  beforeDestroy() {
    this.revokePatientBlobs();
  },

  methods: {
    // ================= TOUR =================
    tourReady() {
      return !this.loadingRelatedPatients;
    },

    tourSteps() {
      return [
        {
          title: "Pacientes da Clínica",
          description:
            "Esta tela mostra, apenas para consulta, todos os pacientes vinculados a esta clínica. Vou te apresentar rapidinho.",
        },
        {
          anchor: "cpa-header",
          title: "Cabeçalho",
          description:
            "Este é o topo do diálogo. O botão de interrogação reabre este tour e o X fecha a janela quando quiser.",
        },
        {
          anchor: "cpa-clinic-info",
          title: "Dados da Clínica",
          description:
            "Este cartão mostra o nome, CNPJ e cidade da clínica que você está visualizando, para confirmar de quais pacientes estamos falando.",
        },
        {
          anchor: "cpa-list",
          title: "Pacientes Vinculados",
          description:
            "Abaixo desta seção ficam listados todos os pacientes já vinculados a esta clínica.",
        },
        {
          anchor: "cpa-patient-card",
          title: "Cartão do Paciente",
          description:
            "Cada paciente é exibido em um cartão. Esta tela é somente para visualização, então aqui você acompanha quem está vinculado à clínica.",
        },
        {
          anchor: "cpa-avatar",
          title: "Foto do Paciente",
          description:
            "A foto de perfil ajuda a reconhecer rapidamente cada paciente da lista.",
        },
        {
          anchor: "cpa-patient-info",
          title: "Dados do Paciente",
          description:
            "Ao lado da foto aparecem o nome, o e-mail e, quando disponível, o CPF do paciente. Pronto, você já conhece a tela!",
        },
      ];
    },

    // ================= CLÍNICAS =================
    async fetchClinics() {
      try {
        this.clinics = (await getMyClinics()) || [];
      } catch (err) {
        console.error("Erro ao buscar clínicas:", err);
        this.erroDeRequisicao(err, "Erro ao carregar clínicas");
        this.clinics = [];
      }
    },

    async initDialog() {
      this.selectedClinicId = this.clinicId;

      this.selectedClinicInfo =
        this.clinics.find((c) => c.id === this.clinicId) || null;

      await this.fetchRelatedPatients();
    },

    // ================= PACIENTES =================
    async fetchRelatedPatients() {
      if (!this.selectedClinicId) return;

      this.loadingRelatedPatients = true;

      // ✅ evita leak ao recarregar lista
      this.revokePatientBlobs();

      try {
        const patients =
          (await getPatientsByClinics(this.selectedClinicId)) || [];

        this.patientsRelated = await Promise.all(
          patients.map(async (p) => {
            const imageUrl = await getProfileImage(p.profile_image);

            // ✅ se for blob:, guarda pra revogar depois
            if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
              if (!this._patientBlobUrls) this._patientBlobUrls = new Set();
              this._patientBlobUrls.add(imageUrl);
            }

            return { ...p, imageUrl };
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar pacientes da clínica:", err);
        this.erroDeRequisicao(err, "Erro ao carregar pacientes da clínica");
        this.patientsRelated = [];
      } finally {
        this.loadingRelatedPatients = false;
      }
    },

    revokePatientBlobs() {
      if (!this._patientBlobUrls) {
        this._patientBlobUrls = new Set();
        return;
      }

      for (const url of this._patientBlobUrls) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }
      this._patientBlobUrls.clear();
    },

    // ================= UI =================
    closeDialog() {
      this.$emit("input", false);
      this.selectedClinicId = null;
      this.selectedClinicInfo = null;
      this.patientsRelated = [];
      this.revokePatientBlobs();
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Info da clínica ═══ */
.clinic-info-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.2px;
}

.clinic-info-meta {
  font-size: 11px;
  color: var(--ds-primary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ═══ Patient card ═══ */
.patient-card {
  border-radius: 12px !important;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.patient-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(31, 77, 63, 0.16) !important;
  border-color: var(--ds-blue-200) !important;
}

.patient-card :deep(.v-avatar) {
  border: 2px solid var(--ds-blue-100);
}

.patient-card :deep(.font-weight-medium) {
  font-size: 13px;
  color: var(--ds-text-strong);
  font-weight: 700 !important;
}

/* ═══ Empty state ═══ */
.empty-state-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ds-primary);
  letter-spacing: -0.2px;
}

.empty-state-subtitle {
  font-size: 12px;
  color: var(--ds-text-soft);
  font-weight: 500;
  line-height: 1.5;
}
</style>
